import { format, parseISO } from 'date-fns'

export const DATE_FMT = "EEEE, MMM d 'at' h:mm a"

export function formatUtcToLocal(iso, fmt = DATE_FMT) {
  if (!iso) return ''
  try {
    const date =
      typeof iso === 'string' || typeof iso === 'number' ? new Date(iso) : iso
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
    return format(date, fmt)
  } catch {
    return 'Invalid Date'
  }
}

/**
 * Safely format a date with error handling and fallback
 * @param {Date|string|number} date - The date to format
 * @param {string} formatString - The date-fns format string
 * @param {string} fallback - The fallback string to return on error (default: 'Invalid Date')
 * @returns {string} Formatted date string or fallback
 */
export function safeFormatDate(date, formatString, fallback = 'Invalid Date') {
  if (date === null || date === undefined || date === '') {
    return fallback
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) {
      return fallback
    }
    return format(dateObj, formatString)
  } catch {
    return fallback
  }
}

/**
 * Format a date/time string for proposal displays with consistent formatting
 * @param {string} dateTimeString - ISO date string
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeYear - Whether to include year in output (default: true)
 * @param {boolean} options.includeTime - Whether to include time in output (default: true)
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(dateTimeString, options = {}) {
  const { includeYear = true, includeTime = true } = options

  if (!dateTimeString) return '—'

  try {
    const dateFormat = includeTime
      ? includeYear
        ? 'EEE, MMM d, yyyy h:mm a'
        : 'EEE, MMM d h:mm a'
      : includeYear
        ? 'MMM d, yyyy'
        : 'MMM d'
    const parsed =
      dateTimeString instanceof Date
        ? dateTimeString
        : parseISO(String(dateTimeString))
    return format(parsed, dateFormat)
  } catch {
    // Fallback to original string if parsing fails
    return dateTimeString || '—'
  }
}

/**
 * Format a date for "Member Since" display
 *
 * Note: This function formats the date in the user's local timezone.
 * For users in timezones significantly different from the server, this may
 * show a different month than when they actually registered (e.g., a user
 * who registered at 2024-06-01 01:00 UTC might see "May 2024" in UTC-8).
 * This is consistent with other date formatting in the app but could be
 * reconsidered if showing the exact server registration date is preferred.
 *
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "June 2024")
 */
export function formatMemberSince(dateString) {
  if (!dateString) return null

  try {
    const parsed =
      dateString instanceof Date ? dateString : parseISO(String(dateString))
    return format(parsed, 'MMMM yyyy')
  } catch {
    return null
  }
}
